import React from 'react'
import { Link } from '@inertiajs/react'
import { HelpTooltip } from '@/components/HelpTooltip'
import { cn } from '@/lib/utils'
import type { HistoryTab, HistoryTabKey } from '@/types/history'

/**
 * A HOTEL'S HISTORY: ONE SIDEBAR ITEM, TWO TABS (docs/plans/2026-10-20-change-safety.md R2.6, X10).
 *
 *   Prices   every price the engine moved (what used to be the Change Log)
 *   Changes  every rule someone changed: who, when, from what to what
 *
 * Page navigation, so a `<nav>` of `<Link>`s styled as tabs, never Radix Tabs (CLAUDE.md). Both the
 * tabs and their hrefs come from the server (`tabs`), and the lit tab is the server's `activeTab`,
 * never a guess from the URL.
 */
export function HistoryTabs({ tabs, activeTab }: { tabs: HistoryTab[]; activeTab: HistoryTabKey }) {
  return (
    <nav
      aria-label="History"
      className="animate-slide-in inline-flex h-10 items-center rounded-md bg-muted p-1 text-muted-foreground"
      style={{ animationDelay: '50ms' }}
    >
      {tabs.map((tab) => {
        const active = tab.key === activeTab
        return (
          <Link
            key={tab.key}
            href={tab.href}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {tab.label}
          </Link>
        )
      })}
    </nav>
  )
}

interface HistoryHeaderProps {
  tabs: HistoryTab[]
  activeTab: HistoryTabKey
}

/**
 * The heading both tabs share, so the two tabs read as one page: the heading, its tooltip and the tab
 * bar. It names no hotel (owner ruling 2026-10-26): the top bar names the hotel and the business.
 */
export default function HistoryHeader({ tabs, activeTab }: HistoryHeaderProps) {
  return (
    <div className="space-y-4">
      <div className="animate-slide-in" style={{ animationDelay: '0ms' }}>
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold tracking-tight text-foreground">History</h1>
          <HelpTooltip
            content="Every price the engine moved, and every rule someone changed."
            learnMore="/guides/history"
          />
        </div>
      </div>
      <HistoryTabs tabs={tabs} activeTab={activeTab} />
    </div>
  )
}
